'use client'

/**
 * UserContext - Backward compatibility wrapper for useAuth
 *
 * This provides a `useCurrentUser` hook that maps to the AuthProvider's user data.
 * Created to maintain backward compatibility with existing components.
 */

import { useAuth } from '@/hooks/useAuth'

interface UseCurrentUserReturn {
  currentUser: ReturnType<typeof useAuth>['user']
  isLoading: ReturnType<typeof useAuth>['isLoading']
  isSwitchedUser: boolean
  switchToUser: (email: string) => Promise<void>
  resetToAuthenticatedUser: () => Promise<void>
}

/**
 * Backward compatibility hook that wraps useAuth
 * Maps AuthProvider's user to currentUser for existing components
 */
export function useCurrentUser(): UseCurrentUserReturn {
  const { user, isLoading } = useAuth()

  // Placeholder implementations for user switching
  // These should be implemented if user switching functionality is needed
  const switchToUser = async (email: string): Promise<void> => {
    console.warn('switchToUser not implemented - consider using ImpersonationProvider')
  }

  const resetToAuthenticatedUser = async (): Promise<void> => {
    console.warn('resetToAuthenticatedUser not implemented - consider using ImpersonationProvider')
  }

  return {
    currentUser: user,
    isLoading,
    isSwitchedUser: false,
    switchToUser,
    resetToAuthenticatedUser
  }
}
